<template>
  <div id="app">
    <nav class="navbar">
      <div class="nav-menu">
        <template v-for="(item, index) in primaryNavItems" :key="'p-' + index">
          <router-link :to="item.path">{{ item.label }}</router-link>
        </template>
        <el-dropdown
          v-if="moreNavItems.length"
          trigger="hover"
          class="nav-more-dropdown"
          popper-class="nav-more-popper"
          @command="goNavPath"
        >
          <span class="nav-more-trigger" :class="{ 'nav-more-trigger--active': isMoreMenuActive }">
            更多
            <el-icon class="nav-more-caret"><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="(item, idx) in moreNavItems"
                :key="'m-' + idx"
                :command="item.path"
              >
                {{ item.label }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
      <div class="nav-actions">
        <template v-if="!isAuthenticated">
          <router-link to="/login">登录</router-link>
          <router-link to="/register">注册</router-link>
        </template>
        <template v-else>
          <el-dropdown @command="handleUserCommand" class="user-dropdown">
            <span class="el-dropdown-link">
              <el-avatar :icon="UserFilled" size="small"></el-avatar>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">个人资料</el-dropdown-item>
                <el-dropdown-item command="changePassword">修改密码</el-dropdown-item>
                <el-dropdown-item command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </div>
    </nav>
    <router-view/>
    <QuestionGenFloatingWidget v-if="isAuthenticated" />
    <QuestionGenTaskPanel v-if="isAuthenticated" />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getUserInfo, getAccessToken, clearUserInfo } from '@/services/auth'
import { UserFilled, ArrowDown } from '@element-plus/icons-vue'
import { hasPermission as checkPermission, checkAnyRole } from '@/utils/permission'
import QuestionGenFloatingWidget from '@/components/QuestionGenFloatingWidget.vue'
import QuestionGenTaskPanel from '@/components/QuestionGenPersistentTaskPanel.vue'
import { useQuestionGenTaskStore } from '@/store'

export default {
  name: 'App',
  components: {
    ArrowDown,
    QuestionGenFloatingWidget,
    QuestionGenTaskPanel
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const questionGenTaskStore = useQuestionGenTaskStore()
    const isAuthenticated = ref(false)

    /** 顶栏直接展示的菜单项数量，其余收入「更多」 */
    const NAV_PRIMARY_MAX = 8

    // 检查用户是否已认证 - 直接从localStorage获取，不依赖store
    const checkAuthStatus = () => {
      const hasToken = !!getAccessToken()
      const hasUser = !!getUserInfo()
      isAuthenticated.value = hasToken && hasUser
    }

    // 简化的权限检查，避免在顶层组件中过度依赖store
    const hasPermission = (permission) => {
      if (!isAuthenticated.value) return false
      
      // 基础权限检查
      try {
        return checkPermission(permission)
      } catch (error) {
        console.warn('权限检查出错:', error)
        return false
      }
    }

    // 角色检查
    const hasRole = (...roles) => {
      if (!isAuthenticated.value) return false
      
      try {
        return checkAnyRole(...roles)
      } catch (error) {
        console.warn('角色检查出错:', error)
        return false
      }
    }

    // 导航项配置
    const navItems = computed(() => {
      const items = [
        { path: '/', label: '首页', visible: true },
        { path: '/courses', label: '我的课程', visible: true },
        { path: '/case-library', label: '案例库', visible: isAuthenticated.value },
        { path: '/certificates', label: '我的证书', visible: true },
        {
          path: '/users',
          label: '人员管理',
          visible: isAuthenticated.value && hasPermission('view_users')
        },
        {
          path: '/admin/work-guidance',
          label: '工作指引管理',
          visible: isAuthenticated.value && hasRole('admin')
        },
        {
          path: '/admin/announcement',
          label: '公告管理',
          visible: isAuthenticated.value && hasRole('admin')
        },
        { 
          path: '/documents', 
          label: '课件资源库', 
          visible: isAuthenticated.value && hasRole('teacher', 'admin')
        },
        { 
          path: '/course-management', 
          label: '课程管理', 
          visible: isAuthenticated.value && hasRole('teacher', 'admin')
        },
        { path: '/exams', label: '考试系统', visible: true },
        {
          path: '/public-questions',
          label: '公开题库',
          visible: isAuthenticated.value
        },
        { 
          path: '/question-bank', 
          label: '题库管理', 
          visible: isAuthenticated.value && hasRole('teacher', 'admin')
        },
        { 
          path: '/exam-papers', 
          label: '试卷管理', 
          visible: isAuthenticated.value && hasRole('teacher', 'admin')
        },
        { 
          path: '/grading', 
          label: '批卷管理', 
          visible: isAuthenticated.value && hasRole('teacher', 'admin')
        },
        { 
          path: '/dashboard', 
          label: '数据看板', 
          visible: isAuthenticated.value && hasRole('teacher', 'admin')
        },
        { 
          path: '/meetings', 
          label: '会议', 
          visible: isAuthenticated.value && hasPermission('view_meetings')
        },
        { 
          path: '/meeting-management', 
          label: '会议管理', 
          visible: isAuthenticated.value && hasRole('teacher', 'admin')
        },
        { path: '/chatbot', label: '知识库问答', visible: true }
      ]
      return items
    })

    const visibleNavItems = computed(() => navItems.value.filter((i) => i.visible))

    const primaryNavItems = computed(() => {
      const v = visibleNavItems.value
      if (v.length <= NAV_PRIMARY_MAX) return v
      return v.slice(0, NAV_PRIMARY_MAX)
    })

    const moreNavItems = computed(() => {
      const v = visibleNavItems.value
      if (v.length <= NAV_PRIMARY_MAX) return []
      return v.slice(NAV_PRIMARY_MAX)
    })

    function routeMatchesNav(current, navPath) {
      if (navPath === '/') return current === '/' || current === ''
      return current === navPath || current.startsWith(`${navPath}/`)
    }

    const isMoreMenuActive = computed(() => {
      const p = route.path
      return moreNavItems.value.some((item) => routeMatchesNav(p, item.path))
    })

    const goNavPath = (path) => {
      if (path) router.push(path)
    }

    // 检查是否有任何可见的导航项（用于决定登录/注册前是否需要分隔符）
    const hasAnyVisibleNavItem = computed(() => visibleNavItems.value.length > 0)

    // 处理用户命令
    const handleUserCommand = (command) => {
      if (command === 'profile') {
        // 跳转到个人资料页面
        router.push('/profile')
      } else if (command === 'changePassword') {
        router.push('/change-password')
      } else if (command === 'logout') {
        // 清除用户信息并跳转到登录页面
        clearUserInfo()
        questionGenTaskStore.clearTask()
        isAuthenticated.value = false
        router.push('/login')
      }
    }

    // 在组件挂载时检查认证状态
    onMounted(() => {
      checkAuthStatus()
      
      // 监听认证状态变化事件
      window.addEventListener('auth-status-changed', checkAuthStatus)
    })
    
    // 在组件卸载前移除事件监听器
    onUnmounted(() => {
      window.removeEventListener('auth-status-changed', checkAuthStatus)
    })

    return {
      isAuthenticated,
      handleUserCommand,
      hasPermission,
      hasRole,
      navItems,
      primaryNavItems,
      moreNavItems,
      isMoreMenuActive,
      goNavPath,
      hasAnyVisibleNavItem,
      UserFilled,
      ArrowDown
    }
  }
}
</script>

<style lang="scss">
@import '@/styles/global.scss';

#app {
  font-family: "Microsoft YaHei", "SimHei", "SimSun", Arial, Helvetica, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: left;
  color: #2c3e50;
}

// 导航栏样式已在 global.scss 中定义，这里只做必要的覆盖
.navbar {
  .nav-more-dropdown {
    flex-shrink: 0;
    vertical-align: middle;
  }

  .nav-more-trigger {
    color: #fff !important;
    font-weight: 600;
    font-size: 15px;
    padding: 16px 14px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    letter-spacing: 0.3px;
    transition: background-color 0.3s ease;
    position: relative;
    user-select: none;

    &:hover {
      background-color: rgba(255, 255, 255, 0.15);
    }
  }

  .nav-more-trigger--active {
    background-color: rgba(255, 255, 255, 0.25);
    font-weight: 700;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background-color: #ff6b35;
    }
  }

  .nav-more-caret {
    font-size: 12px;
    opacity: 0.9;
  }

  // 确保导航栏在移动端也能正常显示
  @media (max-width: 768px) {
    padding: 0 10px;
    
    a {
      font-size: 14px;
      padding: 12px 12px;
    }

    .nav-more-trigger {
      font-size: 14px;
      padding: 12px 12px;
    }
  }
}
</style>
